// @ts-check
/* global mixpanel */
import {Component} from 'panel';

import {addToast} from 'common/components/toast-area/toast-manager';
import {SPECIAL_SERIES_NAMES} from 'common/report/constants';
import {renameProperty, renamePropertyValue} from 'common/report/util/properties';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {CUSTOM_ALERT_FUNNELS_DEFAULT_KEYS} from '../custom-alert/constants';
import template from './index.jade';
import css from './index.styl?inline';

export class ReportConfig extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'bookmark-id': `number`,
      'custom-alerts-disabled-text': {
        type: `string`,
        default: null,
      },
      'display-context': `string`,
      'enable-custom-alert-condition-types': `boolean`,
      'enable-custom-alerts': `boolean`,
      'has-empty-data': `boolean`,
      'project-id': `string`,
      'results-keys-and-values': {type: `json`, default: null},
      'time-unit': `string`,
      'unsaved-changes': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        bookmarkId: null,
        customAlertModalOpen: false,
        open: false,
      },
      helpers: {
        handleClick: () => {
          if (this.state.bookmarkId) {
            mixpanel.track(`[Alerts] Open Alert Menu`, this.trackingProperties());
            this.update({open: !this.state.open});
          }
        },
        getTooltipText: () => {
          return !this.state.bookmarkId || this.attr(`unsaved-changes`)
            ? `To enable alerts, save your report`
            : `Enable Alerts`;
        },
        handleMenuChange: ({detail = {state: null}} = {}) => {
          if (this.state.open && detail.state === `closed`) {
            this.update({open: false});
          }
        },
        handleClickedItemFromAlertBell: (ev) => {
          switch (ev.detail.item.value) {
            case `create`:
              this.helpers.clickedCreateCustomAlert();
              break;
            case `manage`:
              this.helpers.handleManageAlerts(ev);
              window.open(`/settings/account#alert-settings/report/${this.attr(`bookmark-id`)}`);
              break;
          }
        },
        getItemsForAlertBell: () => [
          ...(this.attr(`enable-custom-alerts`)
            ? [
                {
                  label: `Create Alert`,
                  value: `create`,
                  icon: `plus`,
                  isDisabled: this.attr(`has-empty-data`) || this.attr(`custom-alerts-disabled-text`),
                  ...(this.attr(`custom-alerts-disabled-text`)
                    ? {tooltip: {text: this.attr(`custom-alerts-disabled-text`), placement: `left`}}
                    : {}),
                },
              ]
            : []),
          {label: `Manage Alerts`, value: `manage`, icon: `settings`},
        ],
        clickedCreateCustomAlert: () => this.update({open: false, customAlertModalOpen: true}),
        closeCustomAlertModal: () => this.update({customAlertModalOpen: false}),
        handleCustomAlertCreated: () => {
          addToast({
            cta: `Manage Alerts`,
            ctaCallback: () => window.open(`/settings/account#alert-settings`, `_blank`),
            message: `Custom alert created.`,
          });
          this.helpers.closeCustomAlertModal();
        },
        handleCustomAlertError: () => {
          addToast({message: `Failed to create custom alert.`, isError: true});
          this.helpers.closeCustomAlertModal();
        },
        handleManageAlerts: (ev) => {
          ev.stopPropagation();
          if (!ev.detail.modifierKey) {
            this.update({open: false});
          }
        },
        getReportTypeFromContext: () => {
          switch (this.attr(`display-context`)) {
            case `FUNNELS`:
              return `funnels`;
            case `INSIGHTS`:
              return `insights`;
            default:
              return ``;
          }
        },
        isPercentageThreshold: (resultsKeysAndValues) => {
          if (resultsKeysAndValues && resultsKeysAndValues.length > 0) {
            return resultsKeysAndValues[resultsKeysAndValues.length - 1].isPercentage === true;
          } else {
            return false;
          }
        },
        getResultsKeysAndValues: () => {
          switch (this.attr(`display-context`)) {
            case `FUNNELS`:
              return CUSTOM_ALERT_FUNNELS_DEFAULT_KEYS;
            case `INSIGHTS`:
              if (this.attr(`results-keys-and-values`)) {
                return this.attr(`results-keys-and-values`).map((key) => ({
                  ...key,
                  displayName: key.displayName || SPECIAL_SERIES_NAMES[key.name] || renameProperty(key.name),
                  displayValues: key.displayValues || key.values.map((value) => renamePropertyValue(value, key.name)),
                }));
              } else {
                return null;
              }
          }
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `bookmark-id`) {
      this.update({bookmarkId: this.getJSONAttribute(attr)});
    }
  }

  trackingProperties() {
    return {
      'bookmark-id': this.attr(`bookmark-id`),
      report: this.attr(`display-context`),
    };
  }
}

export const ReportConfigEl = defineAndMakeJsxFactory(`mp-report-config`, ReportConfig);
